import {
  Divider,
  Stack,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  HeroBox
} from "./Bookmarks.styles";
import {
  Button,
  Card,
  Carousel,
  GridMain,
  Icon,
  Spinner,
  TabsFilters,
  WidgetDrawer
} from "../../components";
import { BigCarouselCardRestyled } from "../../components/BigCarouselCardRestyle/BigCarouselCardRestyle.component";
import { Carousel as CarouselV2 } from "../../components/Carousel_v2/Carousel.component";
import { CarouselCardRestyle } from "../../components/CarouselCardRestyle/CarouselCardRestyle.component";
import { HeroCard } from "../../components/HeroCard/HeroCard.component";
import { HeroCardBookmarkVariants } from "../../components/HeroCard/HeroCard.variants";
// import * as NewsCardVariants from "../../components/NewsCard/NewsCard.variants";
import {
  CAROUSEL_TYPE,
  CONTENTS_NAME,
  PAGE_NAME,
  STATUS_LO
} from "../../consts";
import { UserNavigationContext } from "../../context/UserNavigationContext";
import {
  useContentQuery,
  useInfiniteContentQuery,
  useStructuresQuery
} from "../../queries";
import { useAuthStore, useLanguageStore } from "../../stores";
import type {
  LearningObject,
  MetaData,
  StatusLO
} from "../../types";
import { createEndDate, createStartDate } from "../../utils/general";
import { CarouselBox } from "../Explore/Explore.styles";

export function Bookmarks() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const setSpinnerLoginVisible = useAuthStore(state => state.setSpinnerLoginVisible);
  const language = useLanguageStore(state => state.language)?.id;
  setSpinnerLoginVisible(false);
  /* const [pageSize, setPageSize] = useState<number>(12); */
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<StatusLO | null | "">(null);
  const { data: pageStructureBookmarks, isFetching:loadingPageStructure } = useStructuresQuery({
    pageName: PAGE_NAME.FAVOURITES_OVERVIEW
  });
  const lastFavouritesObject = pageStructureBookmarks?.relativePaths?.[0];
  const lastFavouritesPath = lastFavouritesObject?.apiPath ?? "";
  const favouritesObject = pageStructureBookmarks?.relativePaths?.[1];
  const favouritesPath = favouritesObject?.apiPath ?? "";
  const ratedObject = pageStructureBookmarks?.relativePaths?.[2];
  const ratedObjectPath = ratedObject?.apiPath ?? "";
  const { setNavigation } = useContext(UserNavigationContext);

  useEffect(() => {
    setNavigation && setNavigation([]);
    setNavigation && setNavigation(["/bookmarks"]);
  }, [location.pathname]);

  const {
    data: lastFavourites,
    isFetching: loadingLastFavourites,
    refetch: refetchLastFavourites
  } = useContentQuery<{
    lastFavouriteLearningObjects: LearningObject[]
    metadata: MetaData
  }>({
    pageSize: 4,
    path: lastFavouritesPath,
    refetchOnMount: true,
    status: ""
  });

  const {
    data: favourites,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching: favouritesAreLoading,
    refetch: refetchFavourites
  } = useInfiniteContentQuery<{
    favouriteLearningObjects: LearningObject[],
    metadata: MetaData
  }>({
    language,
    pageSize: 12,
    path: favouritesPath,
    refetchOnMount: true,
    status: statusFilter
  });

  const {
    data: topRated,
    isFetching: topRatedIsLoading
  } = useContentQuery<{
    topRatedLearningObjects: LearningObject[]
    metadata: MetaData
  }>({
    path: ratedObjectPath,
    refetchOnMount: true,
    status: ""
  });

  const hasFavourites = favourites?.pages?.some((page) =>
    page?.favouriteLearningObjects?.length > 0
  );
  const learnerFavourites = favourites?.pages?.reduce((acc, page) => {
    const contents = page?.[CONTENTS_NAME.FAVOURITES] ?? [];
    return acc.concat(contents);
  }, [] as LearningObject[]);

  // Forza il refetch delle query quando la struttura della pagina è disponibile
  useEffect(() => {
    // Solo quando la struttura è caricata e i path sono disponibili
    if (pageStructureBookmarks && !loadingPageStructure) {
      // Refetch solo se abbiamo un path valido
      if (lastFavouritesPath && lastFavouritesPath !== "") {
        refetchLastFavourites();
      }
      if (favouritesPath && favouritesPath !== "") {
        refetchFavourites();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageStructureBookmarks, loadingPageStructure, lastFavouritesPath, favouritesPath]);


  function handleMobile() {
    if (!isMobile) {
      return (
        <CarouselV2
          hasPagination={ true }
          hasPaginationSweeperBullets={ false }
          isBoxShadowEnabled={ false }
          carouselData={ lastFavourites?.lastFavouriteLearningObjects||[] }
          pauseOnMouseEnter={ true }
          title={ "bigcarousel" }
          slidesPerView={ 1 }
          centeredSlides={ true }
          spaceBetween={ isMobile ? 1 : 11.2 }
          renderElementBigcarouselGridView={ (element) => {
            return (
              <CarouselCardRestyle
                canAccessNow={ element.canAccessNow }
                enrollType={ element.enrollType }
                booked={ element.status !== STATUS_LO.N && element.status !== STATUS_LO.T }
                categories={ element.topicTags || [] }
                coverPublicURL={ element?.coverPublicURL?.toString() || "" }
                coverVideoPublicURL={ element.videoPublicURL }
                disabled={ false }
                duration={ element.duration }
                ecm_specialization={ element.ecm_specialization }
                ecmRegistration={ element.ecmRegistration }
                ecmType={ element.ecmType }
                editionNumber={ element.editionNumber }
                endDate={ createEndDate(element) }
                expirationDate={ element.expirationDate }
                id={ element.id }
                isAutoSubscribeEnable={ element.isAutoSubscribeEnable }
                isFavourite={ element.isFavourite }
                isMandatory={ element.isMandatory }
                isSurvey={ element.isSurvey }
                isEditorial={ element.isEditorial }
                isTest={ element.isTest }
                isToj={ element.isToj }
                key={ `carousel-bookmark-card-${element.id}--${language}` }
                learningObjectType={ element.learningObjectType }
                learningObjectTypology={ element.learningObjectTypology }
                percentageOfCompletion={ element.percentageOfCompletion }
                shortDescription={ element.shortDescription }
                startDate={ createStartDate(element) }
                status={ element.status || "N" }
                title={ element.title }
                isLinkRelative={ element.isLinkRelative }
                link={ element.link }
                attributes={ element.attributes }
                externalFileUrl={ element?.externalFileUrl }
              />
            );
          } }
          renderElement={ (card) => {
            return (
              <BigCarouselCardRestyled
                key={ `explore-hero--card-${card.id}` }
                coverPublicURL={ card.coverPublicURL }
                videoPublicURL={ card.videoPublicURL }
                duration={ card.duration }
                id={ card.id }
                learningObjectTypology={ card.learningObjectTypology }
                shortDescription={ card.shortDescription }
                simple={ true }
                categories={ card.topicTags || [] }
                title={ card.title }
                canAccessNow={ card.canAccessNow }
                percentageOfCompletion={ card.percentageOfCompletion?.slice(0, -1) || "0" }
                learningObjectType={ card.learningObjectType }
                isExpired={ card.isExpired }
                isMandatory={ card.isMandatory }
                expirationDate={ card.expirationDate }
                enrollType={ card.enrollType }
                status={ card.status || "N" }
                endDate={ createEndDate(card) }
                startDate={ createStartDate(card) }
                isToj={ card.isToj }
                isEditorial={ card.isEditorial }
                isHighLightEdition={ card?.editions?.[0]?.isHighlightEdition ?? false }
                labelMoreInfo={ true }
                link={ card.link }
                isLinkRelative={ card.isLinkRelative }
                parentId={ card.parentId }
                editionNumber={ card?.editionNumber }
                grandParentId={ card?.grandParentId }
                isBookmarkPage={ true }
                isStandAlone={ card.isStandAlone }
                isFavourite={ card.isFavourite }
                attributes={ card.attributes }
                isSuggestedAI={ card.isSuggestedAI }
                isSuggestedHR={ card.isSuggestedHR }
                isSuggestedManager={ card.isSuggestedManager }
                badge={ card.badge }
                certificate={ card.certificate }
                ecmRegistration={ card.ecmRegistration }
                ecm_specialization={ card.ecm_specialization }
              />
            );} }
        />
      );
    }else{
      return (
        <CarouselV2
          hasPagination={ true }
          hasPaginationSweeperBullets={ false }
          isBoxShadowEnabled={ false }
          carouselData={ lastFavourites?.lastFavouriteLearningObjects||[] }
          pauseOnMouseEnter={ true }
          title={ "bigcarousel" }
          slidesPerView={ 1 }
          centeredSlides={ true }
          spaceBetween={ isMobile ? 1 : 11.2 }
          renderElement={ (element) => {
            return (
              <CarouselCardRestyle
                canAccessNow={ element.canAccessNow }
                enrollType={ element.enrollType }
                booked={ element.status !== STATUS_LO.N && element.status !== STATUS_LO.T }
                categories={ element.topicTags || [] }
                coverPublicURL={ element?.coverPublicURL?.toString() || "" }
                coverVideoPublicURL={ element.videoPublicURL }
                disabled={ false }
                duration={ element.duration }
                ecm_specialization={ element.ecm_specialization }
                ecmRegistration={ element.ecmRegistration }
                ecmType={ element.ecmType }
                editionNumber={ element.editionNumber }
                endDate={ createEndDate(element) }
                expirationDate={ element.expirationDate }
                id={ element.id }
                isAutoSubscribeEnable={ element.isAutoSubscribeEnable }
                isFavourite={ element.isFavourite }
                isMandatory={ element.isMandatory }
                isSurvey={ element.isSurvey }
                isEditorial={ element.isEditorial }
                isTest={ element.isTest }
                isToj={ element.isToj }
                key={ `carousel-bookmark-card-${element.id}--${language}` }
                learningObjectType={ element.learningObjectType }
                learningObjectTypology={ element.learningObjectTypology }
                percentageOfCompletion={ element.percentageOfCompletion }
                shortDescription={ element.shortDescription }
                startDate={ createStartDate(element) }
                status={ element.status || "N" }
                title={ element.title }
                isLinkRelative={ element.isLinkRelative }
                link={ element.link }
                attributes={ element.attributes }
                externalFileUrl={ element?.externalFileUrl }
              />
            );} }
        />
      );
    }
  }

  return (
    <GridMain>
      <Stack
        maxWidth="calc(100vw - (100vw - 100%))"
        width={ "100%" }
        sx={ {
          ".button-back": {
            color: theme.customColors.textPrimary,
            fontSize: "1.141rem",
            fontWeight: "400",
            gap: "9px",
            justifyContent: "flex-start",
            padding: "32px 16px 37px",
            textAlign: "start",

            "&:hover": {
              background: "transparent"
            },

            ".icon": {
              height: "24px",
              width: "auto"
            }
          }
        } }
      >
        <WidgetDrawer />
        {
          ( loadingLastFavourites ) ?
            (
              <Spinner
                minHeight={ isMobile ? "366px" : "450px" }
                size={ 90 }
                width="100%"
              />
            ) :
            (
              (
                lastFavouritesObject
                && lastFavourites?.lastFavouriteLearningObjects
                && lastFavourites?.lastFavouriteLearningObjects.length > 0
              ) ?
                (
                  <HeroBox role="region" aria-label={ t("last_saved") }>
                    { handleMobile() }
                  </HeroBox>
                ) :  !lastFavourites?.lastFavouriteLearningObjects?.length &&
                !loadingLastFavourites &&
                !favouritesAreLoading &&
                !topRatedIsLoading &&
                !loadingPageStructure &&  (
                  <HeroBox sx={ { alignItems: "center" } }>
                    <HeroCard { ...HeroCardBookmarkVariants } />
                  </HeroBox>
                )
            )
        }
        {
          (
            hasFavourites || statusFilter !== null
          ) ?
            (
              <TabsFilters<StatusLO>
                role="region"
                aria-label={ t("favourite_text") }
                showGridTabView={ false }
                contents={ [
                  {
                    components: [
                      /* eslint-disable react/jsx-key */
                      <Stack>
                        <div style={ { display: "flex", flexWrap: "wrap", flexGrow: 1, gap: "0.7rem",marginTop:"1rem" } }>
                          {
                            learnerFavourites ? (
                              learnerFavourites.map((element) => (
                                <div
                                  style={ isMobile ? { width:"24.5rem", boxSizing: "content-box" }:{  boxSizing: "content-box", rowGap:"0.7rem" }  }
                                  key={ `basic-card-${element.id}` }
                                  role="region"
                                  aria-label={  element.title  }
                                >
                                  <CarouselCardRestyle
                                    key={ `carousel-explore-zoom-5-card-${element.id}--${language}` }
                                    booked={ element.status !== STATUS_LO.N && element.status !== STATUS_LO.T }
                                    categories={ element.topicTags || [] }
                                    coverPublicURL={ element?.coverPublicURL }
                                    coverVideoPublicURL={ element.videoPublicURL }
                                    disabled={ false }
                                    duration={ element.duration }
                                    editionNumber={ element?.editionNumber }
                                    ecmRegistration={ element.ecmRegistration }
                                    ecm_specialization={ element.ecm_specialization }
                                    endDate={ createEndDate(element)  }
                                    enrollType={ element.enrollType }
                                    expirationDate={ element.expirationDate }
                                    grandParentId={ element.grandParentId }
                                    id={ element.id }
                                    isAutoSubscribeEnable={ element.isAutoSubscribeEnable }
                                    isFavourite={ element.isFavourite }
                                    isStandAlone={ element.isStandAlone }
                                    isToj={ element.isToj }
                                    isEditorial={ element.isEditorial }
                                    learningObjectType={ element.learningObjectType }
                                    learningObjectTypology={ element.learningObjectTypology }
                                    parentId={ element.parentId }
                                    percentageOfCompletion={ element.percentageOfCompletion }
                                    shortDescription={ element.shortDescription }
                                    startDate={ createStartDate(element)  }
                                    status={ element.status || "N" }
                                    title={ element.title }
                                    canAccessNow={ element.canAccessNow }
                                    isLinkRelative={ element.isLinkRelative }
                                    link={ element.link }
                                    attributes={ element.attributes }
                                    externalFileUrl={ element?.externalFileUrl }


                                  />
                                </div>
                              ))

                            ) : (
                            // TEMPORARY
                            /* eslint-disable max-len */
                              <Typography>
                                Non abbiamo trovato risultati per i filtri selezionati. Ti consigliamo di rimuovere i filtri o selezionare un altro valore.
                                <Typography
                                  color="primary"
                                  onClick={ () => { setStatusFilter(null)} }
                                  sx={ { cursor: "pointer " } }
                                >
                                  { t("remove_filters") }
                                </Typography>
                              </Typography>
                            )
                          }
                        </div>
                        {
                          (hasNextPage && !isFetchingNextPage) ? (
                            <Button
                              disabled={ !hasNextPage }
                              disableRipple
                              onClick={ () => fetchNextPage() }
                              sx={ {
                                gap:"0.5rem",
                                margin:"0 auto",
                                width:"fit-content"
                              } }
                            >
                              { t("load_more") }
                              <Icon
                                color={ theme.customColors.systemPrimary02 }
                                icon="dropdown"
                                size={ 24 }
                              />
                            </Button>
                          ) : null
                        }
                        {
                          isFetchingNextPage ? (
                            <Spinner />
                          ) : null
                        }
                      </Stack>
                    ],
                    iconTab: "grid"
                  },
                  {
                    components: [
                      /* eslint-disable react/jsx-key */
                      <Stack
                        divider={ <Divider/> }
                        gap={ isMobile ? "1.5rem" : "1rem" }
                        padding={ isMobile ? "30px 0 16px" : "64px 0 16px" }
                        maxWidth="calc(100vw - 2rem)"
                      >
                        {
                          learnerFavourites?.length ? (
                            learnerFavourites.map((card) => (
                              <Card
                                key={ `carousel-explore-zoom-4--card-${card.id}` }
                                booked={ card.status !== STATUS_LO.N && card.status !== STATUS_LO.T }
                                categories={ card.topicTags || [] }
                                coverPublicURL={ card?.coverPublicURL }
                                duration={ card.duration }
                                editionNumber={ card?.editionNumber }
                                endDate={ createEndDate(card)  }
                                ecmRegistration={ card.ecmRegistration }
                                ecm_specialization={ card.ecm_specialization }
                                enrollType={ card.enrollType }
                                expirationDate={ card.expirationDate }
                                grandParentId={ card.grandParentId }
                                parentId={ card.parentId }
                                id={ card.id }
                                isFavourite={ card.isFavourite }
                                learningObjectType={ card.learningObjectType }
                                learningObjectTypology={ card.learningObjectTypology }
                                percentageOfCompletion={ card.percentageOfCompletion }
                                shortDescription={ card.shortDescription }
                                startDate={ createStartDate(card) }
                                status={ card.status || "N" }
                                title={ card.title }
                                canAccessNow={ card.canAccessNow }
                                isLinkRelative={ card.isLinkRelative }
                                link={ card.link }
                              />
                            ))

                          ) : (
                            // TEMPORARY
                            /* eslint-disable max-len */
                            <Typography>
                              Non abbiamo trovato risultati per i filtri selezionati. Ti consigliamo di rimuovere i filtri o selezionare un altro valore. <Typography
                                color="primary"
                                onClick={ () => { setStatusFilter(null)} }
                                sx={ { cursor: "pointer" } }
                              >
                                Rimuovi filtri
                              </Typography>
                            </Typography>
                            /* eslint-enable max-len */
                          )
                        }
                        {
                          (hasNextPage && !isFetchingNextPage) ? (
                            <Button
                              disabled={ !hasNextPage }
                              disableRipple
                              onClick={ () => fetchNextPage() }
                              sx={ {
                                gap:"0.5rem",
                                margin:"0 auto",
                                width:"fit-content"
                              } }
                            >
                              { t("load_more") }
                              <Icon
                                color={ theme.customColors.systemPrimary02 }
                                icon="dropdown"
                                size={ 24 }
                              />
                            </Button>
                          ) : null
                        }
                        {
                          isFetchingNextPage ? (
                            <Spinner />
                          ) : null
                        }
                      </Stack>
                    ],
                    iconTab: "list"
                  }
                ]
                }
                count={ favourites?.pages?.[0]?.metadata.totalElements.toString() }
                // count={ favourites?.pages?.reduce((acc, page) => (
                //   acc + page?.favouriteLearningObjects.length ?? 0
                // ), 0).toString() }
                filter= { statusFilter }
                options={ [
                  {
                    children: t("completed"),
                    value: STATUS_LO.C
                  },
                  {
                    children: t("in_progress"),
                    value: STATUS_LO.P
                  },
                  {
                    children: t("to_start"),
                    value: STATUS_LO.E
                  }
                ] }
                setFilter={ setStatusFilter }
                title={ t("favourite_text") }
              />
            ) : (
              (
                !lastFavourites?.lastFavouriteLearningObjects?.length &&
                !loadingLastFavourites &&
                !favouritesAreLoading &&
                !topRatedIsLoading &&
                !loadingPageStructure &&
                topRated?.topRatedLearningObjects &&
                topRated?.topRatedLearningObjects?.length > 0
              ) ?
                (
                  <CarouselBox>
                    <Carousel
                      carouselType={ CAROUSEL_TYPE.ZOOM }
                      id="bookmarks-empty-zoom-1"
                      label={ ratedObject?.title ?? "" }
                      title={ t("most_rated") }
                      itemsLength={ topRated?.metadata?.totalElements || 0 }
                    >
                      {
                        topRated?.topRatedLearningObjects?.map((card) => (
                          <CarouselCardRestyle
                            key={ `carousel-explore-bookmarks--card-${card.id}--${language}` }
                            booked={ card.status !== STATUS_LO.N && card.status !== STATUS_LO.T }
                            categories={ card.topicTags || [] }
                            coverPublicURL={ card?.coverPublicURL }
                            coverVideoPublicURL={ card.coverVideoPublicUrl }
                            ecmRegistration={ card.ecmRegistration }
                            ecm_specialization={ card.ecm_specialization }
                            disabled={ false }
                            duration={ card.duration }
                            editionNumber={ card?.editionNumber }
                            endDate={ createEndDate(card) }
                            enrollType={ card.enrollType }
                            id={ card.id }
                            isAutoSubscribeEnable={ card.isAutoSubscribeEnable }
                            isFavourite={ card.isFavourite }
                            isToj={ card.isToj }
                            grandParentId={ card.grandParentId }
                            learningObjectType={ card.learningObjectType }
                            learningObjectTypology={ card.learningObjectTypology }
                            parentId={ card.parentId }
                            percentageOfCompletion={ card.percentageOfCompletion }
                            shortDescription={ card.shortDescription }
                            startDate={ createStartDate(card)  }
                            status={ card.status || "N" }
                            title={ card.title }
                            canAccessNow={ card.canAccessNow }
                            isLinkRelative={ card.isLinkRelative }
                            link={ card.link }
                            attributes={ card.attributes }
                            externalFileUrl={ card?.externalFileUrl }
                          />
                        ))
                      }
                    </Carousel>
                  </CarouselBox>
                ) :
                null
            )
        }
      </Stack>
    </GridMain>
  );
}
